import React, { useState, useEffect } from 'react';
import classNames from 'classnames';
import CheckIcon from '../Icons/CheckIcon';
import LinearProgress from './LinearProgress';
import {
  formatBytes,
  getFileExtension,
  getThumbnail,
  isThumbnailAvailable,
  trimFileName,
} from './utils';
import { LOADING_STATUS } from './constants';
import CloseOval from '../Icons/CloseOval';
import CloseIcon from '../Icons/CloseIcon';
import { injectIntl } from 'react-intl';
import { useDispatch, useSelector } from 'react-redux';
import {
  collapseCategoryFileUploader,
  expandCategoryFileUploader,
  hideCategoryFileUploader,
  maximizeFileUploader,
} from '../../actions/mainUI';
import InfoIcon from '../Icons/InfoIcon';
import { OverlayTrigger, Tooltip } from 'react-bootstrap';
import UploadBlack from '../Icons/UploadBlack';
import Loader from '../ui/Loader';

const FilesLoadingIndicator = ({
  fileList,
  setFileList,
  filesLoadingStatus,
  setIsUploadingIndicatorShown,
  intl,
}) => {
  const [selectedFilter, setSelectedFilter] = useState('ALL');
  const [areAllFilesProcessed, setAreAllFilesProcessed] = useState(false);
  const [filteredFileList, setFilteredFileList] = useState([]);
  const [filteredLoadingStatus, setFilteredLoadingStatus] = useState([]);
  const isCollapsed = useSelector((state) => {
    return state.mainUI.getIn(['fileUploader', 'isCollapsed']);
  });
  const categoryNameToUpload = useSelector((state) => {
    return state.mainUI.get('categoryToUpload').categoryName;
  });
  const dispatch = useDispatch();

  useEffect(() => {
    const filteredFileList = fileList.filter((_, fileIndex) => {
      if (selectedFilter === 'ALL') return true;
      if (
        selectedFilter === LOADING_STATUS.FAILED ||
        selectedFilter === LOADING_STATUS.FAILED_DUPLICATED
      )
        return (
          filesLoadingStatus[fileIndex] === LOADING_STATUS.FAILED ||
          filesLoadingStatus[fileIndex] === LOADING_STATUS.FAILED_DUPLICATED
        );
      return filesLoadingStatus[fileIndex] === selectedFilter;
    });

    const filteredLoadingStatus = filesLoadingStatus.filter((_, fileIndex) => {
      if (selectedFilter === 'ALL') return true;
      if (
        selectedFilter === LOADING_STATUS.FAILED ||
        selectedFilter === LOADING_STATUS.FAILED_DUPLICATED
      )
        return (
          filesLoadingStatus[fileIndex] === LOADING_STATUS.FAILED ||
          filesLoadingStatus[fileIndex] === LOADING_STATUS.FAILED_DUPLICATED
        );
      return filesLoadingStatus[fileIndex] === selectedFilter;
    });
    const areAllFilesProcessed = filesLoadingStatus.every(
      (status) =>
        status !== LOADING_STATUS.QUEUED && status !== LOADING_STATUS.UPLOADING
    );
    setFilteredFileList(filteredFileList);
    setFilteredLoadingStatus(filteredLoadingStatus);
    setAreAllFilesProcessed(areAllFilesProcessed);
  }, [selectedFilter, filesLoadingStatus]);

  const getLoadingStatusText = (status) => {
    switch (status) {
      case LOADING_STATUS.QUEUED:
        return intl.formatMessage({
          id: 'fileLoadingIndicator.filterQueued',
        });
      case LOADING_STATUS.UPLOADING:
        return intl.formatMessage({
          id: 'fileLoadingIndicator.filterUploading',
        });
      case LOADING_STATUS.UPLOADED:
        return intl.formatMessage({
          id: 'fileLoadingIndicator.filterUploaded',
        });
      case LOADING_STATUS.FAILED:
        return intl.formatMessage({
          id: 'fileLoadingIndicator.filterFailed',
        });
      case LOADING_STATUS.FAILED_DUPLICATED:
        return intl.formatMessage({
          id: 'fileLoadingIndicator.filterDuplicated',
        });
      default:
        return intl.formatMessage({
          id: 'fileLoadingIndicator.filterQueued',
        });
    }
  };

  const getBadgeStatusColor = (status) => {
    switch (status) {
      case LOADING_STATUS.QUEUED:
        return '#343a40'; // grey
      case LOADING_STATUS.UPLOADING:
        return '#d45083'; // brand-secondary-color
      case LOADING_STATUS.UPLOADED:
        return '#d45083'; // brand-secondary-color
      case LOADING_STATUS.FAILED:
        return '#db0000'; // red
      case LOADING_STATUS.FAILED_DUPLICATED:
        return '#db0000'; // red
      default:
        return '#343a40'; // grey
    }
  };

  const handleFilterClick = (filter) => {
    setSelectedFilter(filter);
  };

  const onClickAddMoreFiles = () => {
    setIsUploadingIndicatorShown(false);
    setFileList([]);
    dispatch(maximizeFileUploader());
  };

  return (
    <div className="files-loader-container">
      <div className="files-loader-header">
        <div className="files-loader-header-text">
          <div>
            {intl.formatMessage({
              id: 'fileLoadingIndicator.uploads',
            })}
          </div>
          <div className="files-loader-header-subtext">
            {categoryNameToUpload}
          </div>
        </div>

        <div className="files-loader-header-close-icon">
          {isCollapsed ? (
            <i
              className="files-loader-icon-uncollapse"
              aria-hidden="true"
              onClick={() => {
                dispatch(expandCategoryFileUploader());
              }}
            ></i>
          ) : (
            <i
              className="files-loader-icon-collapse"
              aria-hidden="true"
              onClick={() => {
                dispatch(collapseCategoryFileUploader());
              }}
            ></i>
          )}
        </div>

        {areAllFilesProcessed && (
          <div
            onClick={() => {
              setIsUploadingIndicatorShown(false);
              setFileList([]);
              dispatch(hideCategoryFileUploader());
            }}
            className="files-loader-header-close-icon"
          >
            <CloseIcon width={24} height={24} aria-label="Close" />
          </div>
        )}
      </div>

      {/* Filter Menu */}
      {!isCollapsed && (
        <div className="files-loader-filter-menu">
          <span
            className={classNames('files-loader-badge', {
              active: selectedFilter === 'ALL',
            })}
            onClick={() => handleFilterClick('ALL')}
          >
            {intl.formatMessage({
              id: 'fileLoadingIndicator.filterAll',
            })}
          </span>
          <span
            className={classNames('files-loader-badge', {
              active: selectedFilter === LOADING_STATUS.QUEUED,
            })}
            onClick={() => handleFilterClick(LOADING_STATUS.QUEUED)}
          >
            {intl.formatMessage({
              id: 'fileLoadingIndicator.filterQueued',
            })}
          </span>
          <span
            className={classNames('files-loader-badge', {
              active: selectedFilter === LOADING_STATUS.UPLOADING,
            })}
            onClick={() => handleFilterClick(LOADING_STATUS.UPLOADING)}
          >
            {intl.formatMessage({
              id: 'fileLoadingIndicator.filterUploading',
            })}
          </span>
          <span
            className={classNames('files-loader-badge', {
              active: selectedFilter === LOADING_STATUS.UPLOADED,
            })}
            onClick={() => handleFilterClick(LOADING_STATUS.UPLOADED)}
          >
            {intl.formatMessage({
              id: 'fileLoadingIndicator.filterUploaded',
            })}
          </span>
          <span
            className={classNames('files-loader-badge', {
              active: selectedFilter === LOADING_STATUS.FAILED,
            })}
            onClick={() => handleFilterClick(LOADING_STATUS.FAILED)}
          >
            {intl.formatMessage({
              id: 'fileLoadingIndicator.filterFailed',
            })}
          </span>
        </div>
      )}

      <div
        className={classNames(
          filteredFileList.length === 0
            ? 'files-loader-center'
            : 'files-loader-wrapper'
        )}
      >
        {filteredFileList.length === 0 ? (
          <div className="filter-menu-no-files-message">
            {intl.formatMessage({
              id: 'fileLoadingIndicator.noFilesFound',
            })}
          </div>
        ) : (
          <div
            className={classNames(
              'files-loader-card-wrapper',
              isCollapsed && 'files-loader-card-wrapper-collapsed'
            )}
          >
            {filteredFileList.map((image, fileIndex) => (
              <div key={fileIndex} className={classNames('files-loader-card')}>
                <div className="files-loader-img-container">
                  {isThumbnailAvailable(image) ? (
                    <img
                      className="files-loader-img "
                      src={getThumbnail(image)}
                      alt="Card image cap"
                      onError={(e) => {
                        e.target.src = './images/thumb/img_nothumb.gif'; // fallback image
                      }}
                    />
                  ) : (
                    <div className="photo-uploader__default-thumbnail photo-uploader__default-thumbnail-loader">
                      <span className="photo-uploader__default-thumbnail-text-loader">
                        {getFileExtension(image.file.name, false)}
                      </span>
                    </div>
                  )}
                  {filteredLoadingStatus[fileIndex] ==
                    LOADING_STATUS.UPLOADED && (
                    <div className="card-img-top photo-uploader__card-check">
                      <CheckIcon
                        fill="#4cd964"
                        width={'18px'}
                        height={'18px'}
                      />
                    </div>
                  )}
                  {(filteredLoadingStatus[fileIndex] == LOADING_STATUS.FAILED ||
                    filteredLoadingStatus[fileIndex] ==
                      LOADING_STATUS.FAILED_DUPLICATED) && (
                    <div className="card-img-top photo-uploader__card-check ">
                      <CloseOval
                        stroke="#db0000"
                        width={'18px'}
                        height={'18px'}
                      />
                    </div>
                  )}
                </div>

                <div className="files-loader-card-body">
                  <div className="files-loader-card-body-inner_wrapper">
                    <div className="files-loader-card-body-content">
                      <h6
                        className="card-title files-loader-card-title"
                        title={image.file.name}
                      >
                        {trimFileName(image.file.name, 15)}
                      </h6>
                      <div className="card-text photo-uploader__card-text">
                        {formatBytes(image.file.size)}
                      </div>
                      <div className="files-loader-card-body-badges">
                        <h4
                          className="files-loader-badge"
                          style={{
                            backgroundColor: '#313030',
                          }}
                        >
                          {getFileExtension(image.file.name)
                            .slice(1)
                            .toUpperCase()}
                        </h4>
                        <h4
                          className="files-loader-badge"
                          style={{
                            backgroundColor: getBadgeStatusColor(
                              filteredLoadingStatus[fileIndex]
                            ),
                          }}
                        >
                          {getLoadingStatusText(
                            filteredLoadingStatus[fileIndex]
                          )}
                        </h4>
                      </div>
                    </div>
                    {(filteredLoadingStatus[fileIndex] ==
                      LOADING_STATUS.FAILED ||
                      filteredLoadingStatus[fileIndex] ==
                        LOADING_STATUS.FAILED_DUPLICATED) && (
                      <div className="files-loader-card-body-info">
                        <OverlayTrigger
                          placement="bottom"
                          delay={{ show: 250, hide: 400 }}
                          overlay={
                            <Tooltip id="tooltip-info">
                              {filteredLoadingStatus[fileIndex] ===
                              LOADING_STATUS.FAILED_DUPLICATED
                                ? intl.formatMessage(
                                    { id: 'fileUploader.nameExists' },
                                    { name: image.file.name }
                                  )
                                : intl.formatMessage(
                                    {
                                      id: 'fileUploader.fileUploadFailFor',
                                    },
                                    {
                                      filename: image.file.name,
                                    }
                                  )}
                            </Tooltip>
                          }
                        >
                          <span>
                            <InfoIcon
                              stroke="#db0000"
                              width={'18px'}
                              height={'18px'}
                            />
                          </span>
                        </OverlayTrigger>
                      </div>
                    )}
                  </div>
                  {filteredLoadingStatus[fileIndex] ==
                    LOADING_STATUS.UPLOADING && <LinearProgress />}
                </div>
              </div>
            ))}
            <div style={{ height: '86px' }}></div>
          </div>
        )}
        <div
          key={'upload-summary'}
          className={classNames(
            'files-loader-card',
            'files-loader-card-summary',
            isCollapsed && 'files-loader-card-summary-collapsed'
          )}
        >
          <div className="files-loader-img-container files-loader-img-container-summary">
            {areAllFilesProcessed ? (
              <UploadBlack stroke="#db0000" width={'32px'} height={'32px'} />
            ) : (
              <Loader
                isLarge={false}
                className="photo-uploader__loader photo-uploader__loader-summary"
              />
            )}
          </div>
          <div className="files-loader-card-body">
            <div className="files-loader-card-body-inner_wrapper">
              <div className="files-loader-card-body-content">
                <h6 className="card-title files-loader-card-title">
                  {areAllFilesProcessed
                    ? intl.formatMessage({
                        id: 'fileLoadingIndicator.uploadComplete',
                      })
                    : `Uploading ${
                        filteredFileList.filter(
                          (file, fileIndex) =>
                            filteredLoadingStatus[fileIndex] !==
                            LOADING_STATUS.QUEUED
                        ).length
                      } of ${fileList.length} items`}
                </h6>
                <div className="card-text photo-uploader__card-text">
                  {intl.formatMessage({
                    id: areAllFilesProcessed
                      ? 'fileLoadingIndicator.pleaseReview'
                      : 'fileLoadingIndicator.pleaseWait',
                  })}
                </div>
              </div>
              {areAllFilesProcessed && !isCollapsed && (
                <div className="files-loader-card-body-info">
                  <div
                    variant="outline"
                    className="files-loader-button"
                    onClick={onClickAddMoreFiles}
                  >
                    {intl.formatMessage({
                      id: 'fileUploader.addMoreLabel',
                    })}
                  </div>
                </div>
              )}
              {isCollapsed && (
                <div
                  className="files-loader-card-body-info"
                  onClick={() => dispatch(expandCategoryFileUploader())}
                >
                  <i
                    className="files-loader-icon-uncollapse"
                    aria-hidden="true"
                  />
                </div>
              )}
              {areAllFilesProcessed && isCollapsed && (
                <div
                  className="files-loader-card-body-info"
                  onClick={() => {
                    setIsUploadingIndicatorShown(false);
                    setFileList([]);
                    dispatch(hideCategoryFileUploader());
                  }}
                >
                  <CloseIcon width={'24px'} height={'24px'} />
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default injectIntl(FilesLoadingIndicator);
